import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import jsPDF from 'jspdf';
import ErrorMessage from '../components/ErrorMessage';
import LoadingProgress from '../components/LoadingProgress';
import FaultsReportFilterForm from '../forms/FaultsReportFilterForm';
import { fetchFaults } from '../actions/faults';
import { fetchReplacement } from '../actions/replacements';
import { fetchCustomers } from '../actions/customers';
import { fetchMatrixs } from '../actions/matrixs';
import { drawReportHeader, drawSectionTitle, drawTable, PDF_MARGIN_X } from '../utils/pdfReport';
import { faultLabel, resolveFaultCode } from '../utils/faults';
import '../styles/FaultsReportView.css';

// All timestamps coming from the API are stored in UTC. The business operates
// in Spain (Europe/Madrid), so every displayed time is rendered in that zone
// — which is UTC+2 in summer — instead of the browser's local / UTC clock.
const SPAIN_TZ = 'Europe/Madrid';

class FaultsReportView extends React.Component {
  constructor(props) {
    super(props);

    this.state = { generating: false };
    this.handleDownloadPDF = this.handleDownloadPDF.bind(this);
  }

  componentDidMount() {
    this.fetchData();
  }

  // Load the reference data needed to render a report, but DO NOT auto-fetch
  // faults: a customer must be picked in the filter first.
  fetchData() {
    this.props.fetchReplacement("64cca358874b8124a31919ab");
    this.props.fetchCustomers();
    this.props.fetchMatrixs();
  }

  getMaterialName(MaterialCode) {
    switch (MaterialCode) {
      case 'LOCK_MAIN':
        return "Centralita"
      case 'LOCK_PINPAD':
        return "Teclado"
      case 'LOCK_ANCHORAGE':
        return "Pincho"
      case 'LOCK_PLATEN':
        return "Pletina"
      case 'LOCK_NUMBER':
        return "Número"
      case 'USB_WIRE':
        return "Cable USB"
      case 'BATTERIES':
        return "Pilas (pack 4)"
      case 'HINGE':
        return "Bisagra"
      case 'DOOR_BNS':
        return "Puerta Standard"
      case 'DOOR_BWS':
        return "Puerta Standard XL"
      case 'DOOR_BNM':
        return "Puerta Medium"
      case 'DOOR_BWM':
        return "Puerta Medium XL"
      case 'DOOR_BNL':
        return "Puerta Large"
      case 'DOOR_BWL':
        return "Puerta Large XL"
      default:
        return "Ajuste sin material"
    }
  }

  getMaterialPrice(MaterialCode) {
    const { replacement } = this.props;

    if (MaterialCode === 'NO_MATERIAL') {
      return 0
    }

    return (replacement && replacement[MaterialCode] && replacement[MaterialCode].Price) || 0
  }

  getMaterialTotalPrice(MaterialCode, Qty) {
    const { replacement } = this.props;

    if (MaterialCode === 'NO_MATERIAL') {
      return 0
    }

    return ((replacement && replacement[MaterialCode] && replacement[MaterialCode].Price) || 0) * Qty
  }

  // Tally the fetched faults by type, most frequent first. A repaired fault only
  // records that a lock broke, so BROKEN_LOCK is split into pincho vs pestillo
  // using the HardType the locker carries in the matrix today — a module swapped
  // to different hardware since the repair would be counted as it is now.
  computeFaultCounts() {
    const { faults } = this.props;

    const counts = new Map();
    (faults || []).forEach((fault) => {
      const locker = this.getMatrixLocker(fault.Serial, fault.Locker);
      const code = fault.FaultCode
        ? resolveFaultCode(fault.FaultCode, locker && locker.HardType)
        : 'UNKNOWN';
      counts.set(code, (counts.get(code) || 0) + 1);
    });

    const total = (faults || []).length;
    const rows = Array.from(counts.entries())
      .map(([code, count]) => ({
        code,
        label: code === 'UNKNOWN' ? 'Sin clasificar' : faultLabel(code),
        count,
        pct: total ? (count / total) * 100 : 0,
      }))
      .sort((a, b) => b.count - a.count);

    return { rows, total };
  }

  // Build the aggregated materials + customer + totals from the fetched faults
  computeReport() {
    const { customers, faults } = this.props;

    const customer = {
      name: '', fiscalName: '', tax: 0, address: '', cif: '',
    };
    let customerAssigned = false;

    const materialsMap = new Map();

    (faults || []).forEach((fault) => {
      if (fault.Materials && Array.isArray(fault.Materials) && fault.Materials.length > 0) {
        fault.Materials.forEach((material) => {
          let { MaterialCode, Qty } = material;

          if (MaterialCode === 'DOOR') {
            MaterialCode = `DOOR_${fault.LockerType}`;
          }

          if (!materialsMap.has(MaterialCode)) {
            materialsMap.set(MaterialCode, { Qty, Total: Qty });
          } else {
            const existingMaterial = materialsMap.get(MaterialCode);
            existingMaterial.Qty += Qty;
            existingMaterial.Total += Qty;
          }

          if (!customerAssigned && customers) {
            const match = customers.find((c) => c._id === fault.Machine.Customer);
            if (match) {
              customer.name = match.Fullname;
              customer.fiscalName = match.Social_name;
              customer.tax = match.Invoice_tax;
              customer.address = match.Address;
              customer.cif = match.Invoice_taxid;
              customerAssigned = true;
            }
          }
        });
      }
    });

    const vatRate = (Number(customer.tax) || 0) / 100;
    let subtotal = 0;
    Array.from(materialsMap.entries()).forEach(([MaterialCode, material]) => {
      subtotal += this.getMaterialTotalPrice(MaterialCode, material.Qty);
    });

    const base = subtotal;
    const total = subtotal * (1 + vatRate);
    const vat = total - base;

    return { materialsMap, customer, vatRate, base, vat, total };
  }

  renderFaultReport() {
    const { faults } = this.props;

    if (!Array.isArray(faults)) {
      return null;
    }

    const { materialsMap, customer, vatRate, base, vat, total } = this.computeReport();

    return (
      <>
        {/* KPI summary */}
        <div className="report-kpis">
          <div className="report-kpi k-count">
            <div className="report-kpi__label">Actuaciones</div>
            <div className="report-kpi__value">{faults.length}</div>
          </div>
          <div className="report-kpi k-base">
            <div className="report-kpi__label">Base</div>
            <div className="report-kpi__value">{base.toFixed(2)} €</div>
          </div>
          <div className="report-kpi k-vat">
            <div className="report-kpi__label">IVA ({(vatRate * 100).toFixed(0)}%)</div>
            <div className="report-kpi__value">{vat.toFixed(2)} €</div>
          </div>
          <div className="report-kpi k-total">
            <div className="report-kpi__label">Total</div>
            <div className="report-kpi__value">{total.toFixed(2)} €</div>
          </div>
        </div>

        {/* Client info */}
        <div className="report-card">
          <div className="report-card__head">
            <i className="fas fa-user"></i>Cliente
          </div>
          <div className="report-info">
            <div><div className="k">Cliente</div><div className="v">{customer.name || '—'}</div></div>
            <div><div className="k">Nombre Fiscal</div><div className="v">{customer.fiscalName || '—'}</div></div>
            <div><div className="k">CIF</div><div className="v">{customer.cif || '—'}</div></div>
            <div><div className="k">Dirección</div><div className="v">{customer.address || '—'}</div></div>
          </div>
        </div>

        {/* Materials cost */}
        <div className="report-card">
          <div className="report-card__head">
            <i className="fas fa-boxes"></i>Coste material de reparación
            <span className="count-pill">{materialsMap.size} materiales</span>
          </div>
          <div className="report-table-wrap">
            <table className="report-table">
              <thead>
                <tr>
                  <th>Material</th>
                  <th className="num">Uds</th>
                  <th className="num">Precio/ud</th>
                  <th className="num">Total</th>
                </tr>
              </thead>
              <tbody>
                {Array.from(materialsMap.entries()).map(([MaterialCode, material]) => (
                  <tr key={MaterialCode}>
                    <td>{this.getMaterialName(MaterialCode)}</td>
                    <td className="num">{material.Qty}</td>
                    <td className="num">{this.getMaterialPrice(MaterialCode).toFixed(2)} €</td>
                    <td className="num">{this.getMaterialTotalPrice(MaterialCode, material.Qty).toFixed(2)} €</td>
                  </tr>
                ))}
                <tr className="sub-row">
                  <td colSpan="3" className="num">Base</td>
                  <td className="num">{base.toFixed(2)} €</td>
                </tr>
                <tr className="sub-row">
                  <td colSpan="3" className="num">IVA ({(vatRate * 100).toFixed(0)}%)</td>
                  <td className="num">{vat.toFixed(2)} €</td>
                </tr>
                <tr className="total-row">
                  <td colSpan="3" className="num">TOTAL</td>
                  <td className="num">{total.toFixed(2)} €</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  }

  renderFaults() {
    const { customers, replacement, matrixs, faults, error, loading } = this.props;

    if (error) {
      return <ErrorMessage message={error.message} />;
    }

    // Base reference data or a faults request still in flight
    if (!customers || !replacement || !matrixs || loading) {
      return <LoadingProgress />
    }

    // No customer picked / report not generated yet
    if (!Array.isArray(faults)) {
      return (
        <div className="report-empty">
          <i className="fas fa-hand-pointer"></i>
          Selecciona un cliente y pulsa <strong>Generar</strong> para ver el informe
        </div>
      );
    }

    if (faults.length === 0) {
      return (
        <div className="report-empty">
          <i className="fas fa-info-circle"></i>
          No hay reparaciones para la fecha, cliente o máquina solicitados
        </div>
      );
    }

    return (
      <div>{this.renderFaultReport()}</div>
    )
  }

  formatDate(timestamp) {
    if (!timestamp) return '—';

    const options = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: SPAIN_TZ,
    };

    return new Intl.DateTimeFormat('es-ES', options).format(new Date(timestamp));
  }

  // A fault only stores its locker's serial (BLEName); everything else about that
  // locker — its position in the machine, its hardware — lives in the matrix.
  getMatrixLocker(serial, lockerSN) {
    const { matrixs } = this.props;

    const matrix = (matrixs || []).find((m) => m.Serial === serial);
    if (!matrix) return null;

    return (matrix.Locker || []).find((locker) => locker.BLEName === lockerSN) || null;
  }

  getMatrixIndex(firstindex, serial, lockerSN) {
    const { matrixs } = this.props;

    const matrix = (matrixs || []).find((m) => m.Serial === serial);
    const lockerIndex = matrix
      ? (matrix.Locker || []).findIndex((locker) => locker.BLEName === lockerSN)
      : -1;

    // An unmatched locker falls back to the machine's first one, as before.
    return firstindex + Math.max(lockerIndex, 0)
  }

  renderFaultMachineReport() {
    const { faults } = this.props;

    if (!Array.isArray(faults)) {
      return null;
    }

    return (
      <div className="report-card">
        <div className="report-card__head">
          <i className="fas fa-tools"></i>Resumen de actuaciones
          <span className="count-pill">{faults.length}</span>
        </div>
        <div className="report-table-wrap">
          <table className="report-table">
            <thead>
              <tr>
                <th>Fecha/Hora Avería</th>
                <th>Fecha/Hora Reparación</th>
                <th>Máquina</th>
                <th className="num">Taquilla</th>
                <th>SN</th>
                <th>Material</th>
              </tr>
            </thead>
            <tbody>
              {faults.map((fault, index) => (
                <tr key={index}>
                  <td className="mono">{this.formatDate(fault.InitTimeStamp)}</td>
                  <td className="mono">{this.formatDate(fault.EndTimeStamp)}</td>
                  <td>{fault.Serial}</td>
                  <td className="num">{this.getMatrixIndex(fault.Machine.FirstIndex, fault.Serial, fault.Locker)}</td>
                  <td className="mono">{fault.Locker}</td>
                  <td>
                    {fault.Materials && fault.Materials.length > 0
                      ? fault.Materials.map((material, matIndex) => (
                          <span key={matIndex} className="mat-chip">
                            {this.getMaterialName(material.MaterialCode)}: {material.Qty}
                          </span>
                        ))
                      : <span className="text-muted">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  renderFaultsMachine() {
    const { matrixs, customers, replacement, faults, loading } = this.props;

    if (
      loading ||
      !matrixs || !customers || !replacement ||
      !Array.isArray(faults) || faults.length === 0
    ) {
      return null;
    }

    return (
      <div>{this.renderFaultMachineReport()}</div>
    )
  }

  // Compact date/time for PDF table cells (Spain time)
  formatDatePdf(timestamp) {
    if (!timestamp) return '—';
    return new Intl.DateTimeFormat('es-ES', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', timeZone: SPAIN_TZ,
    }).format(new Date(timestamp));
  }

  // Build the repair report as a real, text-based PDF document.
  buildPdf() {
    const { faults } = this.props;
    const { materialsMap, customer, vatRate, base, vat, total } = this.computeReport();

    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const marginX = PDF_MARGIN_X;
    let y = drawReportHeader(doc, 'Informe de Reparación');

    const fecha = new Date().toLocaleString('es-ES', { timeZone: SPAIN_TZ });
    doc.text(`Cliente: ${customer.name || '-'}`, marginX, y); y += 14;
    doc.text(`Nombre Fiscal: ${customer.fiscalName || '-'}`, marginX, y); y += 14;
    doc.text(`CIF: ${customer.cif || '-'}`, marginX, y); y += 14;
    doc.text(`Dirección: ${customer.address || '-'}`, marginX, y); y += 14;
    doc.text(`Fecha: ${fecha}`, marginX, y); y += 26;

    // Fault counter — how many of each type of fault the report covers
    const { rows: faultRows, total: faultTotal } = this.computeFaultCounts();
    if (faultRows.length > 0) {
      y = drawSectionTitle(doc, y, 'Resumen de averías');

      const faultCols = [
        { title: 'Avería', width: 305, align: 'left' },
        { title: 'Nº', width: 105, align: 'right' },
        { title: '% del total', width: 105, align: 'right' },
      ];
      const faultTableRows = faultRows.map((r) => ({
        cells: [r.label, String(r.count), `${r.pct.toFixed(1)} %`],
      }));
      faultTableRows.push({
        cells: ['TOTAL AVERÍAS', String(faultTotal), '100.0 %'],
        bold: true,
      });

      y = drawTable(doc, y, faultCols, faultTableRows, { fontSize: 9 });
      y += 26;
    }

    // Materials table
    y = drawSectionTitle(doc, y, 'Coste material de reparación');

    const matCols = [
      { title: 'Material', width: 245, align: 'left' },
      { title: 'Uds', width: 60, align: 'right' },
      { title: 'Precio/ud', width: 100, align: 'right' },
      { title: 'Total', width: 110, align: 'right' },
    ];
    const matRows = Array.from(materialsMap.entries()).map(([code, m]) => ({
      cells: [
        this.getMaterialName(code),
        String(m.Qty),
        `${this.getMaterialPrice(code).toFixed(2)} €`,
        `${this.getMaterialTotalPrice(code, m.Qty).toFixed(2)} €`,
      ],
    }));
    matRows.push({ cells: ['', '', 'Base:', `${base.toFixed(2)} €`] });
    matRows.push({ cells: ['', '', `IVA (${(vatRate * 100).toFixed(0)}%):`, `${vat.toFixed(2)} €`] });
    matRows.push({ cells: ['', '', 'TOTAL:', `${total.toFixed(2)} €`], bold: true });

    y = drawTable(doc, y, matCols, matRows, { fontSize: 9 });
    y += 26;

    // Actuaciones table
    if (y > doc.internal.pageSize.getHeight() - 90) {
      doc.addPage();
      y = 40;
    }
    y = drawSectionTitle(doc, y, 'Resumen de actuaciones');

    const actCols = [
      { title: 'F. Avería', width: 95, align: 'left' },
      { title: 'F. Reparación', width: 95, align: 'left' },
      { title: 'Máquina', width: 70, align: 'left' },
      { title: 'Taquilla', width: 50, align: 'right' },
      { title: 'SN', width: 90, align: 'left' },
      { title: 'Material', width: 115, align: 'left' },
    ];
    const actRows = (faults || []).map((f) => ({
      cells: [
        this.formatDatePdf(f.InitTimeStamp),
        this.formatDatePdf(f.EndTimeStamp),
        f.Serial || '',
        String(this.getMatrixIndex(f.Machine.FirstIndex, f.Serial, f.Locker)),
        f.Locker || '',
        (f.Materials || [])
          .map((mat) => `${this.getMaterialName(mat.MaterialCode)}: ${mat.Qty}`)
          .join('\n') || '—',
      ],
    }));
    y = drawTable(doc, y, actCols, actRows, { fontSize: 8, lineH: 11 });

    return { doc, customer };
  }

  handleDownloadPDF() {
    if (this.state.generating) return;
    const { faults } = this.props;
    if (!Array.isArray(faults) || faults.length === 0) return;

    this.setState({ generating: true });
    try {
      const { doc, customer } = this.buildPdf();
      const safe = (customer.name || 'cliente').replace(/[^a-z0-9]+/gi, '_');
      const date = new Date().toISOString().slice(0, 10);
      doc.save(`INFORME_REPARACION_${safe}_${date}.pdf`);
    } catch (err) {
      console.error('Repair report PDF error:', err);
    } finally {
      this.setState({ generating: false });
    }
  }

  render() {
    const { generating } = this.state;
    const { faults } = this.props;
    const hasReport = Array.isArray(faults) && faults.length > 0;

    return (
      <div className="report-view">
        {/* Header — title, subtle filters and download all in one row */}
        <div className="report-header no_print">
          <div className="report-header__bar">
            <h1 className="report-header__title">
              <i className="fas fa-tools"></i>
              Informe de reparación
            </h1>
            <FaultsReportFilterForm />
            <div className="rf-btn-col">
              <span className="rf-cap-spacer" aria-hidden="true"></span>
              <button
                type="button"
                className="report-btn report-btn--download"
                onClick={this.handleDownloadPDF}
                disabled={generating || !hasReport}
                title={hasReport ? 'Descargar informe en PDF' : 'Genera un informe primero'}
              >
                {generating ? (
                  <><i className="fas fa-spinner fa-spin"></i>Generando…</>
                ) : (
                  <><i className="fas fa-file-pdf"></i>Descargar PDF</>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Report content */}
        <div id="printable-content">
          {this.renderFaults()}
          {this.renderFaultsMachine()}
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  replacement: state.replacements.replacement,
  matrixs: state.matrixs.matrixs,
  faults: state.faults.faults,
  customers: state.customers.customers,
  error: state.faults.error,
  loading: state.faults.loading,
  form: state.form
});

const mapDispatchToProps = dispatch => ({
  fetchFaults: bindActionCreators(fetchFaults, dispatch),
  fetchReplacement: bindActionCreators(fetchReplacement, dispatch),
  fetchCustomers: bindActionCreators(fetchCustomers, dispatch),
  fetchMatrixs: bindActionCreators(fetchMatrixs, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(FaultsReportView);
